import { useEffect, useState } from 'react';
import { Alert, IconButton, Snackbar, Tooltip } from '@mui/material';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import {
  atendimentoHumanoDisponivel,
  marcarAvisoMinimizadoDado,
  montarWebchat,
  reabrirWebchat,
  registrarSinalDoChat,
  webchatMontado,
} from '../../lib/webchat-suporte';

type Aviso = { texto: string; severidade: 'warning' | 'error' | 'info' };

const FORA_DO_HORARIO = 'Fora do horário de atendimento: deixe sua mensagem que um atendente responde no próximo horário.';
const FALHOU = 'Não foi possível abrir o chat do suporte. Confira a internet e tente de novo.';
const GUARDADO = 'O chat está no fone lá em cima. Aperte nele para ver se o suporte respondeu.';
const GUARDADO_FORA_DO_HORARIO = 'Fora do horário: o suporte responde no próximo atendimento. O chat está no fone lá em cima.';
// Ao abrir, o widget pisca close/open no mesmo segundo: só avisa o close que fica.
const ESPERA_AVISO_MINIMIZADO_MS = 800;

/** Fone do topo do backoffice: abre o chat na própria tela em qualquer aparelho, como no PDV. */
export function BotaoSuporte(): JSX.Element {
  const [aviso, setAviso] = useState<Aviso | null>(null);

  useEffect(() => {
    let espera: ReturnType<typeof setTimeout> | undefined;
    function aoReceberMensagem(evento: MessageEvent<unknown>): void {
      const sinal = registrarSinalDoChat(evento);
      if (sinal === null) return;
      clearTimeout(espera);
      if (sinal === 'abriu') return;
      espera = setTimeout(() => {
        marcarAvisoMinimizadoDado();
        // No celular o aviso de fora do horário da abertura fica atrás do chat em tela cheia: repete aqui.
        const foraDoHorario = !atendimentoHumanoDisponivel(new Date());
        setAviso(
          foraDoHorario
            ? { texto: GUARDADO_FORA_DO_HORARIO, severidade: 'warning' }
            : { texto: GUARDADO, severidade: 'info' },
        );
      }, ESPERA_AVISO_MINIMIZADO_MS);
    }
    window.addEventListener('message', aoReceberMensagem);
    return () => {
      clearTimeout(espera);
      window.removeEventListener('message', aoReceberMensagem);
    };
  }, []);

  function abrirChat(): void {
    if (!webchatMontado()) {
      if (!atendimentoHumanoDisponivel(new Date())) setAviso({ texto: FORA_DO_HORARIO, severidade: 'warning' });
      montarWebchat(() => {
        setAviso({ texto: FALHOU, severidade: 'error' });
      });
      return;
    }
    if (!reabrirWebchat()) setAviso({ texto: 'Abrindo o chat…', severidade: 'info' });
  }

  return (
    <>
      <Tooltip title="Converse com um atendente">
        <IconButton color="inherit" onClick={abrirChat} aria-label="Converse com um atendente">
          <HeadsetMicIcon />
        </IconButton>
      </Tooltip>
      <Snackbar
        open={aviso !== null}
        autoHideDuration={6000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={aviso?.severidade ?? 'info'} onClose={() => setAviso(null)} variant="filled">
          {aviso?.texto}
        </Alert>
      </Snackbar>
    </>
  );
}
